import React, { useEffect, useMemo, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Mail as MailIcon,
  AccountCircle as AccountCircleIcon,
  Logout as LogoutIcon,
  Message as MessageIcon,
  Public as PublicIcon,
  Article as ArticleIcon,
  ContactMail as ContactMailIcon,
  History as HistoryIcon,
  AccountBalanceWallet as WalletIcon,
  Category as CategoryIcon,
  BugReport as BugReportIcon,
  MonetizationOn as EarnIcon,
  Payment as PaymentIcon,
  VolunteerActivism as DonationIcon,
  VerifiedUser as VerifiedIcon,
  Shield as ShieldIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { accountService } from '../../services/accountService';
import { getVisibleTabKeys } from '../../services/configService';
import {
  isMinorNavTabVisible,
  sanitizeMinorAllowedTabsForRuntime,
} from '../../utils/minorTabPolicy';
import FeedbackDialog from '../FeedbackDialog';
import PasskeyPortabilityBanner from '../auth/PasskeyPortabilityBanner';
import StoreSafeModeBanner from './StoreSafeModeBanner';
import NativeOfflineOverlay from './NativeOfflineOverlay';
import NativeBiometricUnlockOverlay from './NativeBiometricUnlockOverlay';
import BiometricEnablePrompt from './BiometricEnablePrompt';
import { isStoreDistribution, isNativeShell } from '../../utils/distribution';
import { applyNativeDisplayDensityToDocument, loadNativeDisplayDensity } from '../../utils/nativeDisplayPreferences';
import { useNativeCompactLandscape } from '../../hooks/useNativeCompactLandscape';

interface LayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 200; // Reduced from 240

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasAdvancedMailbox, isMinor, minorAllowedTabs } = useAuth();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const storeMode = isStoreDistribution();
  const nativeShell = isNativeShell();
  const compactLandscape = useNativeCompactLandscape();

  useEffect(() => {
    if (nativeShell) {
      document.documentElement.classList.add('qixit-native-shell');
      applyNativeDisplayDensityToDocument(loadNativeDisplayDensity());

      const syncNativeHeaderMetrics = () => {
        const landscapePhone = window.matchMedia('(orientation: landscape) and (max-height: 520px)').matches;
        if (!landscapePhone) {
          document.documentElement.style.setProperty('--qixit-toolbar-height', storeMode ? '64px' : '56px');
        } else {
          document.documentElement.style.removeProperty('--qixit-toolbar-height');
        }
        document.documentElement.style.setProperty(
          '--qixit-app-bar-height',
          'calc(var(--app-safe-top, env(safe-area-inset-top, 0px)) + var(--qixit-toolbar-height, 56px))',
        );
        document.documentElement.style.setProperty('--qixit-app-header', 'var(--qixit-app-bar-height)');
      };

      syncNativeHeaderMetrics();
      window.addEventListener('resize', syncNativeHeaderMetrics);
      window.addEventListener('orientationchange', syncNativeHeaderMetrics);
      return () => {
        document.documentElement.classList.remove('qixit-native-shell');
        window.removeEventListener('resize', syncNativeHeaderMetrics);
        window.removeEventListener('orientationchange', syncNativeHeaderMetrics);
      };
    }
    return undefined;
  }, [nativeShell, storeMode]);

  // Fetch account balance for header display
  // Include username in query key so each user gets their own cached balance
  const { data: balance, isLoading: balanceLoading } = useQuery({
    queryKey: ['accountBalance', user?.username],
    queryFn: () => accountService.getBalance(),
    staleTime: 5000, // Keep header balance fresh
    refetchInterval: 15000, // Refresh frequently for new credits/debits
    refetchOnWindowFocus: true,
    enabled: !!user?.username, // Only fetch if user is logged in
  });

  // Visible tabs config: tabs visible to normal users (admin-configurable). Advanced users see all.
  const { data: visibleTabKeys = [] } = useQuery({
    queryKey: ['visibleTabKeys'],
    queryFn: getVisibleTabKeys,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!user?.username,
  });

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Menu items with stable keys for visible-tabs config. Show if hasAdvancedMailbox OR tab is in visible-to-normal list.
  const allMenuItems = [
    { key: 'messages', text: 'Messages', icon: <MessageIcon />, path: '/messages' },
    { key: 'address-book', text: 'Address Book', icon: <ContactMailIcon />, path: '/address-book' },
    { key: 'category-settings', text: 'Commercial Postage Rates', icon: <CategoryIcon />, path: '/category-settings' },
    { key: 'account-history', text: 'Account History', icon: <HistoryIcon />, path: '/account-history' },
    { key: 'payments', text: 'Payments', icon: <PaymentIcon />, path: '/payments' },
    { key: 'donation-settings', text: 'Donation Settings', icon: <DonationIcon />, path: '/donation-settings' },
    { key: 'earn-credits', text: 'Earn Credits', icon: <EarnIcon />, path: '/earn-credits' },
    { key: 'verification', text: 'Verification', icon: <VerifiedIcon />, path: '/verification' },
    { key: 'my-id-verification-requests', text: 'My ID Verification Requests', icon: <ShieldIcon />, path: '/my-id-verification-requests' },
    { key: 'trust-hub', text: 'Trust Hub', icon: <ShieldIcon />, path: '/trust-hub' },
    { key: 'profile', text: 'My Profile', icon: <AccountCircleIcon />, path: '/profile' },
    { key: 'public-profiles', text: 'Public Profiles', icon: <PublicIcon />, path: '/public-profiles' },
    { key: 'social-posts', text: 'Social Posts', icon: <ArticleIcon />, path: '/social-posts' },
    { key: null, text: 'Support / Report Bug', icon: <BugReportIcon />, path: null, action: 'feedback' as const },
  ];

  const effectiveMinorAllowedTabs = useMemo(
    () =>
      isMinor
        ? sanitizeMinorAllowedTabsForRuntime(minorAllowedTabs, visibleTabKeys, hasAdvancedMailbox)
        : [],
    [isMinor, minorAllowedTabs, visibleTabKeys, hasAdvancedMailbox],
  );

  const menuItems = allMenuItems.filter((item) => {
    if (item.action === 'feedback') return true;
    const key = item.key as string;

    if (storeMode && (key === 'payments' || key === 'earn-credits')) {
      return false;
    }

    if (isMinor) {
      return isMinorNavTabVisible(key, effectiveMinorAllowedTabs);
    }

    return hasAdvancedMailbox || (visibleTabKeys.length > 0 ? visibleTabKeys.includes(key) : true);
  });

  const handleNavClick = (item: typeof menuItems[0]) => {
    if (item.action === 'feedback') {
      setFeedbackOpen(true);
    } else if (item.path) {
      navigate(item.path);
    }
    if (isMobile) setMobileOpen(false);
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.key ?? item.text} disablePadding>
            <ListItemButton
              selected={item.path ? location.pathname === item.path : false}
              onClick={() => handleNavClick(item)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: item.path && location.pathname === item.path ? 'inherit' : 'action.active' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', ...(nativeShell ? { height: '100dvh', overflow: 'hidden' } : {}) }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          paddingTop: 'var(--app-safe-top, env(safe-area-inset-top, 0px))',
        }}
      >
        <Toolbar
          sx={{
            minHeight: compactLandscape ? 'var(--qixit-toolbar-height, 28px)' : { xs: 56, sm: 64 },
            height: compactLandscape ? 'var(--qixit-toolbar-height, 28px)' : undefined,
            py: compactLandscape ? 0 : undefined,
            px: compactLandscape ? 0.5 : undefined,
          }}
        >
          {/* Hamburger — visible on mobile only */}
          <IconButton
            color="inherit"
            onClick={() => setMobileOpen(true)}
            sx={{
              display: { md: 'none' },
              mr: compactLandscape ? 0.25 : 1,
              p: compactLandscape ? 0.25 : 1.25,
              minWidth: compactLandscape ? 28 : 48,
              minHeight: compactLandscape ? 28 : 48,
            }}
            aria-label="open navigation"
          >
            <MenuIcon sx={{ fontSize: compactLandscape ? 20 : 24 }} />
          </IconButton>

          <MailIcon sx={{ mr: { xs: 0.5, sm: 2 }, display: compactLandscape ? 'none' : 'block' }} />
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              minWidth: 0,
              fontSize: compactLandscape ? '0.8125rem' : undefined,
              lineHeight: compactLandscape ? 1.1 : undefined,
              overflow: compactLandscape ? 'hidden' : undefined,
              textOverflow: compactLandscape ? 'ellipsis' : undefined,
              whiteSpace: compactLandscape ? 'nowrap' : undefined,
            }}
          >
            Mailbox
            {user?.username && (
              <Typography
                component="span"
                variant="subtitle1"
                sx={{
                  ml: compactLandscape ? 0.75 : 2,
                  fontWeight: 'normal',
                  opacity: 0.9,
                  display: 'inline',
                  fontSize: compactLandscape ? '0.8125rem' : undefined,
                }}
                title={
                  (user.loginHandle || user.username).trim().toLowerCase() !== user.username.trim().toLowerCase()
                    ? `${user.username} (logged in as ${user.loginHandle || user.username})`
                    : user.username
                }
              >
                —{' '}
                {(user.loginHandle || user.username).trim().toLowerCase() !== user.username.trim().toLowerCase()
                  ? `${user.username} (logged in as ${user.loginHandle})`
                  : user.username}
              </Typography>
            )}
          </Typography>

          <Box sx={{ display: compactLandscape ? 'none' : 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            {balanceLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : balance ? (
              <Tooltip title="Click to view account history" arrow>
                <Box component="span" sx={{ display: 'contents' }}>
                  <Typography
                    variant="caption"
                    sx={{ display: { xs: 'none', sm: 'block' }, opacity: 0.85, fontWeight: 600 }}
                  >
                    Qix Credits
                  </Typography>
                  {/* Full balance widget — sm and above */}
                  <Box
                    onClick={() => navigate('/account-history')}
                    sx={{
                      display: { xs: 'none', sm: 'flex' },
                      alignItems: 'center',
                      gap: 3,
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    <WalletIcon sx={{ fontSize: 20, opacity: 0.8 }} />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', lineHeight: 1 }}>
                        Account Balance
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                        ${balance.actualBalance.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', lineHeight: 1 }}>
                        Reserved
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 'bold', lineHeight: 1.2, color: balance.pendingBalance > 0 ? '#ffcdd2' : 'inherit' }}
                      >
                        ${balance.pendingBalance.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', lineHeight: 1 }}>
                        Available
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2, color: '#b9f6ca' }}>
                        ${balance.availableBalance.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Compact balance widget — xs only */}
                  <Box
                    onClick={() => navigate('/account-history')}
                    sx={{
                      display: { xs: 'flex', sm: 'none' },
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    <WalletIcon sx={{ fontSize: 18, opacity: 0.8 }} />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', lineHeight: 1, fontSize: '0.6rem' }}>
                        Available
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', lineHeight: 1.2, fontSize: '0.75rem', color: '#b9f6ca' }}>
                        ${balance.availableBalance.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Tooltip>
            ) : null}

            <IconButton color="inherit" onClick={handleLogout} title="Logout">
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0 }}>
        {/* Mobile temporary drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              top: 'var(--app-safe-top, env(safe-area-inset-top, 0px))',
            },
          }}
        >
          {/* Spacer so list starts below AppBar */}
          <Box sx={{ height: `calc(56px + var(--app-safe-top, env(safe-area-inset-top, 0px)) + ${storeMode ? 8 : 0}px)` }} />
          {drawerContent}
        </Drawer>

        {/* Desktop permanent drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              top: 64,
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            p: nativeShell && compactLandscape ? 0 : nativeShell ? { xs: 0.5, sm: 2, md: 3 } : { xs: 1, sm: 2, md: 3 },
            mt: {
              xs: nativeShell
                ? 'var(--qixit-app-bar-height, calc(56px + var(--app-safe-top, env(safe-area-inset-top, 0px))))'
                : 'calc(56px + var(--app-safe-top, env(safe-area-inset-top, 0px)))',
              md: 8,
            },
            minWidth: 0,
            minHeight: 0,
            ...(nativeShell
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }
              : {}),
          }}
        >
          <StoreSafeModeBanner />
          <NativeOfflineOverlay />
          <NativeBiometricUnlockOverlay />
          <BiometricEnablePrompt />
          {!compactLandscape && <PasskeyPortabilityBanner />}
          {nativeShell ? (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
              }}
            >
              {children}
            </Box>
          ) : (
            children
          )}
        </Box>
      </Box>

      {/* Feedback Dialog */}
      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        sourcePortal="mailbox-web"
        summaUsername={user?.username}
      />
    </Box>
  );
};

export default Layout;

